import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBillingBanner } from '../../lib/bannerProviders/billing';
import { getLegalInventoryBanner } from '../../lib/bannerProviders/legalInventory';
import { getNotificationBanner } from '../../lib/bannerProviders/notifications';
import type { BannerContent, BannerTone } from '../../lib/bannerProviders/types';

const TONE_CLASS: Record<BannerTone, string> = {
  neutral: 'border-ui-border bg-ui-surface-2',
  warning: 'border-ui-warning/40 bg-ui-warning/10',
  danger: 'border-ui-danger/40 bg-ui-danger/10',
};

interface AppBannerProps {
  // saas_admin no tiene tenant propio: solo consulta el proveedor genérico
  // (avisos de plataforma). Todos los demás roles ven billing primero.
  isSaasAdmin: boolean;
}

// Reemplaza el mount directo de BillingBanner (GOL-08A). Lista de proveedores por
// prioridad — se renderiza el primero que devuelve contenido, nunca dos a la vez.
export default function AppBanner({ isSaasAdmin }: AppBannerProps) {
  const navigate = useNavigate();
  const [banner, setBanner] = useState<BannerContent | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const providers = isSaasAdmin
      ? [() => getLegalInventoryBanner(navigate), () => getNotificationBanner(navigate)]
      : [() => getBillingBanner(navigate), () => getNotificationBanner(navigate)];
    for (const provider of providers) {
      const result = await provider();
      if (result) { setBanner(result); return; }
    }
    setBanner(null);
  }, [isSaasAdmin, navigate]);

  useEffect(() => { void Promise.resolve().then(() => load()); }, [load]);

  if (!banner) return null;

  async function handleAction() {
    setBusy(true);
    try {
      await banner!.onAction();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`mb-4 flex flex-col gap-3 rounded-xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${TONE_CLASS[banner.tone]}`}>
      <span className="text-sm text-ui-text">{banner.message}</span>
      <button onClick={handleAction} disabled={busy} className="min-h-11 shrink-0 rounded-lg bg-ui-accent px-4 text-sm font-medium text-ui-text-inverse transition hover:bg-ui-accent-hover disabled:opacity-60">
        {busy ? 'Abriendo…' : banner.actionLabel}
      </button>
    </div>
  );
}
